/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  ATTACHMENT_CONFIG_ATTRIBUTES,
  specialDocumentsInAnotherLanguage,
  specialDocumentTypes,
  translationDocuments,
} from '../../services/constants';
import { showAlertError } from '../../../../../redux/features/alerts/alerts.slice';
import { createMessage } from '../../../../../redux/features/alerts/alerts.helper';
import {
  filterAttachmentConfigByType,
  getAttachmentConfig,
  getAttachmentConfigValue,
} from '../../services/attachments.helper';
import { useAttachmentsConfig } from '../../hooks/useAttachmentsConfig';

export const useAttachmentMetaFields = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { attachmentTypesConfig } = useAttachmentsConfig();

  const isAttachmentWithClaims = (attachmentSubType) => {
    const claimsTypes = [specialDocumentTypes.CLAIMS, specialDocumentTypes.DIFFERENT_CLAIMS, translationDocuments.TRANSLATION_CLAIMS, specialDocumentsInAnotherLanguage.CLAIMS_IN_ANOTHER_LANGUAGE];
    return claimsTypes.includes(attachmentSubType);
  };

  const isAttachmentWithDrawings = (attachmentSubType) => {
    const drawingsTypes = [specialDocumentTypes.DRAWINGS, specialDocumentTypes.TRANSLATION_DRAWINGS, specialDocumentsInAnotherLanguage.DRAWINGS_IN_ANOTHER_LANGUAGE];
    return drawingsTypes.includes(attachmentSubType);
  };

  const isAttachmentWithAbstract = (attachmentSubType) => {
    const abstractTypes = [specialDocumentTypes.ABSTRACT, translationDocuments.TRANSLATION_ABSTRACT, specialDocumentsInAnotherLanguage.ABSTRACT_IN_ANOTHER_LANGUAGE];
    return abstractTypes.includes(attachmentSubType);
  };

  const extractAttachmentMetadata = (attachmentFile, getValues, jsonSchema) => {
    if (getValues().numberOfClaims) {
      attachmentFile.numberOfClaims = Number.parseInt(getValues().numberOfClaims);
    }
    if (getValues().numberOfDrawings) {
      attachmentFile.numberOfDrawings = Number.parseInt(getValues().numberOfDrawings);
    }
    if (getValues().numberOfNewClaims) {
      attachmentFile.numberOfNewClaims = Number.parseInt(getValues().numberOfNewClaims);
    }
    if (getValues().totalNumberOfPreviouslyDeclaredClaims) {
      attachmentFile.totalNumberOfPreviouslyDeclaredClaims = Number.parseInt(
        getValues().totalNumberOfPreviouslyDeclaredClaims,
      );
    }
    if (getValues().documentType) {
      attachmentFile.documentType = getValues().documentType;
    }
    if (getValues().figureToBePublished) {
      attachmentFile.figureToBePublished = getValues().figureToBePublished;
    }

    const attachmentConfig = getAttachmentConfig(
      attachmentTypesConfig,
      attachmentFile.attachmentSubType,
      attachmentFile.attachmentType,
    );

    const numberOfSubTypes = getAttachmentConfigValue(
      attachmentConfig,
      ATTACHMENT_CONFIG_ATTRIBUTES.MIN_NUMBER_OF_SUB_TYPES,
    );

    if (jsonSchema) {
      const dropdownEnumName = extractDropdownDefinition(attachmentConfig, jsonSchema)?.name;
      if (dropdownEnumName && getValues()[dropdownEnumName]) {
        attachmentFile[dropdownEnumName] = getValues()[dropdownEnumName];
      }
    }

    if (attachmentFile.attachmentType === specialDocumentTypes.COMBINED) {
      attachmentFile.combinedFile = {
        combinedFileTypeScopes: [],
      };

      attachmentTypesConfig
        .map(docType => docType.type)
        .forEach((type) => {
          const typeValue = getValues(type);
          if (typeValue) {
            const typeScope = {
              type,
              start: parseInt(getValues(`${type}from`)),
              end: parseInt(getValues(`${type}to`)),
            };
            if (isAttachmentWithClaims(type)) {
              typeScope.numberOfClaims = parseInt(getValues(`${type}numberOfClaims`));
            }
            if (isAttachmentWithDrawings(type)) {
              typeScope.numberOfDrawings = parseInt(getValues(`${type}numberOfDrawings`));
            }
            if (isAttachmentWithAbstract(type)) {
              typeScope.figureToBePublished = getValues(`${type}figureToBePublished`);
            }
            attachmentFile.combinedFile.combinedFileTypeScopes.push(typeScope);
          }
        });

      if (attachmentFile.combinedFile.combinedFileTypeScopes.length < numberOfSubTypes) {
        if (numberOfSubTypes === ATTACHMENT_CONFIG_ATTRIBUTES.MIN_NUMBER_OF_SUB_TYPES.default) {
          dispatch(
            showAlertError(createMessage(t('attachments.warning.combined-file-must-contain-two-attachment-types'))),
          );
        } else {
          dispatch(
            showAlertError(
              createMessage(
                t('attachments.warning.combined-file-must-contain-any-attachment-types', { number: numberOfSubTypes }),
              ),
            ),
          );
        }
        return undefined;
      }
    }
    return attachmentFile;
  };

  const extractDropdownDefinition = (attachmentConfig, schema) => {
    const dropdownDefinitionName = getAttachmentConfigValue(
      attachmentConfig,
      ATTACHMENT_CONFIG_ATTRIBUTES.DROPDOWN_DEFINITION_NAME,
    );

    return dropdownDefinitionName && schema?.definitions ? schema?.definitions[dropdownDefinitionName] : null;
  };

  const extractAllDropdownNames = (attachmentType, jsonSchema) => {
    const attachmentsConfig = filterAttachmentConfigByType(attachmentTypesConfig, attachmentType);
    const allDropdownFields = attachmentsConfig
      ?.map((config) => {
        if (config.dropdownDefinition) {
          return extractDropdownDefinition(config, jsonSchema)?.name;
        }
        return null;
      })
      .filter(definition => definition);
    return [...new Set(allDropdownFields)];
  };

  return {
    isAttachmentWithClaims,
    isAttachmentWithDrawings,
    isAttachmentWithAbstract,
    extractAttachmentMetadata,
    extractDropdownDefinition,
    extractAllDropdownNames,
  };
};
